import React, { useContext, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { 
    BarChart3, 
    Calendar, 
    ChevronDown, 
    Crown, 
    Filter, 
    Loader2, 
    Medal,
    Percent, 
    Plus, 
    Sparkles, 
    Star, 
    TrendingUp, 
    UserRound, 
    Users, 
    BadgeIndianRupee
} from "lucide-react";
import { BASE_URL_API } from "../../References/URLs";
import ActiveStoreContext from "../../store/ActiveStoreContext";
import AuthContext from "../../store/auth-context";
import styles from "./StarPerformers.module.css";

const StarPerformers = () => {
    const authCtx = useContext(AuthContext);
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = (() => {
        try {
            return asCtx?.activeStore?.trim()
                ? JSON.parse(asCtx.activeStore)
                : null;
        } catch {
            return null;
        }
    })();

    /* ── state ── */
    const [performanceData, setPerformanceData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [storeTotalSales, setStoreTotalSales] = useState(0);
    const [totalStaff, setTotalStaff] = useState(0);
    const [filterApplied, setFilterApplied] = useState(null);
    const [filterMode, setFilterMode] = useState('preset');
    const [preset, setPreset] = useState('thisMonth');
    const [showFilters, setShowFilters] = useState(false);

    /* ── filter options ── */
    const presetOptions = [
        { value: 'today', label: 'Today', icon: Calendar },
        { value: 'yesterday', label: 'Yesterday', icon: Calendar },
        { value: 'thisWeek', label: 'This Week', icon: Calendar },
        { value: 'lastWeek', label: 'Last Week', icon: Calendar },
        { value: 'thisMonth', label: 'This Month', icon: Calendar },
        { value: 'lastMonth', label: 'Last Month', icon: Calendar },
        { value: 'last7days', label: 'Last 7 Days', icon: TrendingUp },
        { value: 'last30days', label: 'Last 30 Days', icon: TrendingUp },
        { value: 'last90days', label: 'Last 90 Days', icon: TrendingUp },
        { value: 'thisFiscalYear', label: 'This Fiscal Year', icon: BarChart3 },
        { value: 'thisFiscalQuarter', label: 'This Fiscal Quarter', icon: BarChart3 },
        { value: 'thisFiscalMonth', label: 'This Fiscal Month', icon: BarChart3 }
    ];

    const getStaffPerformanceSummary = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(BASE_URL_API + "getStaffPerformanceSummary", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    filterMode,
                    preset: filterMode === 'preset' ? preset : undefined,
                }),
            });

            if (!response.ok) throw new Error('Network response was not ok');

            const data = await response.json();
            if (data.status === "success") {
                setPerformanceData(data.response || []);
                setStoreTotalSales(data.storeTotalSales || 0);
                setTotalStaff(data.totalStaff || 0);
                setFilterApplied(data.filterApplied || null);
            } else if (data.message === "tokenExpired") {
                authCtx.logout();
            } else {
                setPerformanceData([]);
                setStoreTotalSales(0);
                setTotalStaff(0);
            }
        } catch (error) {
            console.error('Error fetching staff performance:', error);
            setPerformanceData([]);
            setStoreTotalSales(0);
            setTotalStaff(0);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (activeStore?.storeIdentifier) {
            getStaffPerformanceSummary();
        }
    }, [activeStore?.storeIdentifier, filterMode, preset]);

    /* ── analytics ── */
    const analytics = useMemo(() => {
        if (!performanceData.length) {
            return {
                topPerformer: null,
                averageSales: 0,
                totalBookings: 0,
                averageContribution: 0,
                performanceDistribution: []
            };
        }

        const totalBookings = performanceData.reduce((sum, staff) => sum + staff.totalBookings, 0);
        const averageSales = storeTotalSales / Math.max(totalStaff, 1);
        const averageContribution = 100 / Math.max(totalStaff, 1);

        const performanceDistribution = performanceData.reduce((acc, staff) => {
            const tier = staff.revenueContributionPct >= 40 ? 'High' :
                        staff.revenueContributionPct >= 20 ? 'Medium' : 'Low';
            acc[tier] = (acc[tier] || 0) + 1;
            return acc;
        }, {});

        return {
            topPerformer: performanceData[0] || null,
            averageSales,
            totalBookings,
            averageContribution,
            performanceDistribution
        };
    }, [performanceData, storeTotalSales, totalStaff]);

    const getGenderIcon = (gender) => {
        return gender === "Male" ? 
            <UserRound size={14} className={styles.genderIconMale} /> : 
            <UserRound size={14} className={styles.genderIconFemale} />;
    };

    const getRankBadge = (rank) => {
        if (rank === 1) return <Crown size={16} className={styles.crownIcon} />;
        if (rank === 2) return <Medal size={14} className={styles.silverMedal} />;
        if (rank === 3) return <Medal size={14} className={styles.bronzeMedal} />;
        return <span className={styles.rankNumber}>#{rank}</span>;
    };

    const getContributionColor = (percentage) => {
        if (percentage >= 40) return styles.contributionHigh;
        if (percentage >= 20) return styles.contributionMedium;
        return styles.contributionLow;
    };

    const formatCurrency = (amount) => {
        const num = parseFloat(amount) || 0;
        return num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    const formatPeriodLabel = () => {
        if (!filterApplied) return 'Performance Overview';
        
        if (filterApplied.mode === 'preset') {
            const selectedPreset = presetOptions.find(p => p.value === filterApplied.preset);
            return selectedPreset?.label || 'Performance Overview';
        }

        if (filterApplied.from && filterApplied.to) {
            if (filterApplied.from === filterApplied.to) {
                return `${filterApplied.from}`;
            }
            return `${filterApplied.from} - ${filterApplied.to}`;
        }

        return 'Performance Overview';
    };

    if (!activeStore) {
        return (
            <div className={styles.noStoreWrapper}>
                <div className={styles.noStoreCard}>
                    <Users size={32} color="#6366f1" />
                    <h3>No Store Selected</h3>
                    <p>Please select a store to view staff performance data.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.mainWrapper}>
            {/* Header Section */}
            <div className={styles.headerSection}>
                <div className={styles.titleArea}>
                    <div className={styles.titleIcon}>
                        <Sparkles size={18} />
                    </div>
                    <div>
                        <h1 className={styles.pageTitle}>Star Performers</h1>
                        <p className={styles.pageSubtitle}>
                            {formatPeriodLabel()} · {totalStaff} Active Staff
                        </p>
                    </div>
                </div>

                <div className={styles.filterArea}>
                    <button 
                        className={`${styles.filterToggle} ${showFilters ? styles.filterToggleActive : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <Filter size={14} />
                        Filters
                        <ChevronDown size={12} className={showFilters ? styles.chevronUp : ''} />
                    </button>
                </div>
            </div>

            {/* Filter Panel */}
            {showFilters && (
                <motion.div 
                    className={styles.filterPanel}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                >
                    <div className={styles.filterRow}>
                        <label className={styles.filterLabel}>Quick Periods</label>
                        <div className={styles.presetGrid}>
                            {presetOptions.map((option) => {
                                const IconComponent = option.icon;
                                return (
                                    <button
                                        key={option.value}
                                        className={`${styles.presetButton} ${preset === option.value ? styles.presetButtonActive : ''}`}
                                        onClick={() => {
                                            setFilterMode('preset');
                                            setPreset(option.value);
                                        }}
                                    >
                                        <IconComponent size={12} />
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Loading State */}
            {isLoading && (
                <div className={styles.loadingWrapper}>
                    <Loader2 size={28} className={styles.loadingSpinner} />
                    <p className={styles.loadingText}>Analyzing staff performance...</p>
                </div>
            )}

            {/* Empty State */}
            {!isLoading && performanceData.length === 0 && (
                <div className={styles.emptyWrapper}>
                    <div className={styles.emptyCard}>
                        <Users size={40} color="#94a3b8" />
                        <h3>No Performance Data</h3>
                        <p>No staff activity found for the selected period. Try a different date range.</p>
                    </div>
                </div>
            )}

            {/* Main Content */}
            {!isLoading && performanceData.length > 0 && (
                <>
                    {/* KPI Cards */}
                    <div className={styles.kpiGrid}>
                        <motion.div 
                            className={styles.kpiCard}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                        >
                            <div className={styles.kpiIcon}>
                                <BadgeIndianRupee size={16} />
                            </div>
                            <div className={styles.kpiContent}>
                                <div className={styles.kpiValue}>₹{formatCurrency(storeTotalSales)}</div>
                                <div className={styles.kpiLabel}>Total Revenue</div>
                            </div>
                        </motion.div>

                        <motion.div 
                            className={styles.kpiCard}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            <div className={styles.kpiIcon}>
                                <Users size={16} />
                            </div>
                            <div className={styles.kpiContent}>
                                <div className={styles.kpiValue}>{totalStaff}</div>
                                <div className={styles.kpiLabel}>Active Staff</div>
                            </div>
                        </motion.div>

                        <motion.div 
                            className={styles.kpiCard}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <div className={styles.kpiIcon}>
                                <TrendingUp size={16} />
                            </div>
                            <div className={styles.kpiContent}>
                                <div className={styles.kpiValue}>₹{formatCurrency(analytics.averageSales)}</div>
                                <div className={styles.kpiLabel}>Avg per Staff</div>
                            </div>
                        </motion.div>

                        <motion.div 
                            className={styles.kpiCard}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                        >
                            <div className={styles.kpiIcon}>
                                <Plus size={16} />
                            </div>
                            <div className={styles.kpiContent}>
                                <div className={styles.kpiValue}>{analytics.totalBookings}</div>
                                <div className={styles.kpiLabel}>Total Bookings</div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Top Performer Spotlight */}
                    {analytics.topPerformer && (
                        <motion.div 
                            className={styles.spotlightCard}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.5 }}
                        >
                            <div className={styles.spotlightHeader}>
                                <div className={styles.spotlightIcon}>
                                    <Crown size={18} />
                                </div>
                                <div>
                                    <h2 className={styles.spotlightTitle}>Top Performer</h2>
                                    <p className={styles.spotlightSubtitle}>Leading the team in revenue generation</p>
                                </div>
                            </div>

                            <div className={styles.spotlightBody}>
                                <div className={styles.performerInfo}>
                                    <div className={styles.performerAvatar}>
                                        {getGenderIcon(analytics.topPerformer.servedByGender)}
                                    </div>
                                    <div className={styles.performerDetails}>
                                        <h3 className={styles.performerName}>{analytics.topPerformer.servedByName}</h3>
                                        <p className={styles.performerMeta}>{analytics.topPerformer.servedByGender}</p>
                                    </div>
                                </div>

                                <div className={styles.performerStats}>
                                    <div className={styles.performerStat}>
                                        <div className={styles.performerStatValue}>₹{formatCurrency(analytics.topPerformer.totalSales)}</div>
                                        <div className={styles.performerStatLabel}>Revenue</div>
                                    </div>
                                    <div className={styles.performerStat}>
                                        <div className={styles.performerStatValue}>{analytics.topPerformer.totalBookings}</div>
                                        <div className={styles.performerStatLabel}>Bookings</div>
                                    </div>
                                    <div className={styles.performerStat}>
                                        <div className={`${styles.performerStatValue} ${getContributionColor(analytics.topPerformer.revenueContributionPct)}`}>
                                            {analytics.topPerformer.revenueContributionPct.toFixed(1)}%
                                        </div>
                                        <div className={styles.performerStatLabel}>Contribution</div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Performance Leaderboard */}
                    <motion.div 
                        className={styles.leaderboardCard}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                    >
                        <div className={styles.leaderboardHeader}>
                            <h3 className={styles.leaderboardTitle}>
                                <Medal size={16} />
                                Performance Leaderboard
                            </h3>
                            <p className={styles.leaderboardSubtitle}>
                                Ranked by total revenue contribution
                            </p>
                        </div>

                        <div className={styles.leaderboardBody}>
                            {performanceData.map((staff, index) => (
                                <motion.div
                                    key={staff.servedByIdentifier}
                                    className={`${styles.leaderboardRow} ${index < 3 ? styles.leaderboardRowTop : ''}`}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.7 + index * 0.05 }}
                                >
                                    <div className={styles.staffRank}>
                                        {getRankBadge(index + 1)}
                                    </div>

                                    <div className={styles.staffInfo}>
                                        <div className={styles.staffAvatar}>
                                            {getGenderIcon(staff.servedByGender)}
                                        </div>
                                        <div className={styles.staffDetails}>
                                            <div className={styles.staffName}>{staff.servedByName}</div>
                                            <div className={styles.staffMeta}>
                                                {staff.totalCartItems} Services · {staff.totalAddOns} Add-ons
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.staffMetrics}>
                                        <div className={styles.staffRevenue}>₹{formatCurrency(staff.totalSales)}</div>
                                        <div className={styles.staffBookings}>{staff.totalBookings} bookings</div>
                                    </div>

                                    <div className={styles.staffContribution}>
                                        <div className={`${styles.contributionBadge} ${getContributionColor(staff.revenueContributionPct)}`}>
                                            <Percent size={12} />
                                            {staff.revenueContributionPct.toFixed(1)}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </>
            )}
        </div>
    );
};

export default StarPerformers;
